import { Box, IconButton } from "@mui/material";
import { JSONTree } from "react-json-tree";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import xmlFormat from "xml-formatter";

const styles = {
  wrapper: {
    display: "flex",
    alignItems: "start",
  },
  container: {
    p: 1,
    flexGrow: 1,
    wordWrap: "break-word",
    display: "inline-block",
    width: 0,
  },
  copyButton: {
    marginTop: 2,
    marginLeft: -5,
  },
  title: {
    mb: 1,
    fontWeight: "bold",
  },
  xmlContainer: {
    whiteSpace: "pre-wrap",
  },
};

const isXml = (text?: string) => text && text.startsWith("<");
const isJsonObj = (text?: string) => text && text.startsWith("{");

const safeFormatXml = (value: string): string =>
  xmlFormat(value, { throwOnFailure: false });

const tryParseJson = (value: string): object | null => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const copyToClipboard = (value?: string) => {
  if (!value) {
    return;
  }

  if (isJsonObj(value)) {
    const parsed = tryParseJson(value);
    if (parsed) {
      value = JSON.stringify(parsed, null, 4);
    }
  } else if (isXml(value)) {
    value = safeFormatXml(value);
  }

  navigator.clipboard.writeText(value);
};

type GenericContentBoxProps = {
  value?: string;
  color: string;
  title: string;
};

export default function GenericContentBox({
  value,
  color,
  title,
}: GenericContentBoxProps) {
  if (!value) {
    return null;
  }

  const parsedJson = isJsonObj(value) ? tryParseJson(value) : null;

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.container} bgcolor={color}>
        {parsedJson ? (
          <JSONTree
            data={parsedJson}
            theme={{ base00: color }}
            labelRenderer={([key]) => (key === "root" ? title : key)}
          />
        ) : (
          <>
            <Box sx={styles.title}>{title}</Box>
            {isXml(value) ? (
              <Box component="pre" sx={styles.xmlContainer}>
                {safeFormatXml(value)}
              </Box>
            ) : (
              value
            )}
          </>
        )}
      </Box>
      <IconButton
        sx={styles.copyButton}
        size="small"
        onClick={() => copyToClipboard(value)}
      >
        <FileCopyIcon />
      </IconButton>
    </Box>
  );
}
